/**
 * Brand Setup — the wizard's whole state, as a pure module.
 *
 * Everything the five screens collect lives here, and everything the study
 * proposes lands here through `applyStudy`. The screens render state and
 * dispatch actions; `payload.ts` turns the final state into the SAME
 * `CreateChannelInput` the classic wizard posts. Keeping the two pure is what
 * lets the create payload be unit-tested without a browser.
 *
 * Colour model: Brand Setup collapses the classic wizard's four colour fields
 * into FOUR ROLES the user can actually reason about —
 *   Accent  → brand.accent + brand.captionAccent + brand.speakerNameColor
 *   Ink     → brand.captionColor
 *   Paper   → brand.paper   (post canvas; no video render effect yet)
 *   Muted   → brand.muted   (same scope)
 * Font model: TWO roles — Display (speaker name + punch line) and Body
 * (captions + the card's role line).
 */
import type { EditPatch } from "../../../../src/lib/clipEditState.ts";
import type { BrandStudyRecord, StudyChip } from "../../api";
import { DEFAULT_LANGUAGE, LANGUAGE_PRESETS, directionForCode, makeCustomLanguage, type LanguagePreset } from "../languages";
import type { StepId } from "./steps";

/**
 * Which screen we are on. The LIST of screens, their order, their numbering
 * and where Continue/Back go all live in `steps.ts` — state only remembers the
 * id. (This used to be a numeric union with a `BRAND_SETUP_STEPS` table; the
 * stepper replaced five broad screens with one decision per screen, and a
 * number can neither name a lane-specific step nor survive a draft restore.)
 *
 * @deprecated Transitional alias for the screens still typing against the old
 * name. Import `StepId` from `./steps` instead.
 */
export type BrandSetupStep = StepId;

/** A thing the user handed us to read. `assetKind` is UI-only (it picks the
 *  icon and the "reel"/"post" word); the server source shape drops it. */
export type BrandSetupSource =
  | { kind: "url"; url: string }
  | { kind: "upload"; uploadId: string; filename: string; assetKind: "video" | "image" };

/** Caption pacing, same shape as `CreateChannelInput["cueShape"]`. */
export interface BrandSetupCueShape {
  maxWords?: number;
  maxChars?: number;
  maxDurSec?: number;
  accentWords?: 0 | 1 | 2;
}

export interface BrandSetupState {
  step: StepId;
  /**
   * Where Continue AND Back go, when the operator got here from Review's
   * "Change" (spec §2.1). Null on every ordinary walk. Kept in state rather
   * than in the screen because a draft restore has to bring it back — landing
   * on `colors` with no way home would strand the operator mid-review.
   */
  returnTo: StepId | null;
  /** "Keep all and continue" was taken on `reading`: the studied steps are
   *  done, so `logo` hands straight to Review (§2.2). */
  keptAll: boolean;
  /** null until the user picks a lane on the Source screen. */
  mode: "auto" | "manual" | null;
  brandName: string;
  slug: string;
  /** True once the user edits the slug by hand — the name stops driving it. */
  slugTouched: boolean;
  language: LanguagePreset;
  sources: BrandSetupSource[];
  studyId?: string;
  study?: BrandStudyRecord;
  colors: { accent: string; ink: string; paper: string; muted: string };
  /** Which curated pair the Colors step has lit up — a preset id, `"custom"`,
   *  or null before anything is picked. A LABEL, never a value: `colors` above
   *  stays the only thing the payload reads. */
  colorPair: string | null;
  /** Family names ("" = keep the founding default for that role). */
  fonts: { display: string; body: string; uploaded?: { dataUrl: string; family: string } };
  /**
   * The speaker step's "Same as captions" row. While true the reducer MIRRORS
   * `fonts.body` into `fonts.display` on every change, so `payload.ts` keeps
   * reading exactly one field and can never ship a stale display face. Picking
   * a face for the name by hand turns it off — that is a different answer to
   * the same question.
   */
  displaySame: boolean;
  /** CSS letter-spacing in px (negative tightens). */
  trackingPx: number;
  logoDataUrl?: string;
  /** How the logo on screen was made. `"wordmark"` means we drew it from the
   *  name (§2.5) — a draft restore redraws it from `wordmark` instead of
   *  re-asking, and an upload's bytes are simply gone. */
  logoKind: "upload" | "wordmark" | null;
  /** What the wordmark was drawn FROM — the parameters, not the pixels, so a
   *  restored draft (or a name/colour change) can redraw it. */
  wordmark?: { text: string; fontFamily: string; color: string };
  /**
   * What the STEPS decided — the collapsed geometry of the walk itself, laid
   * over the base layout. Written only by `layoutPatch` (a step card) and by
   * the study.
   */
  designPatch?: EditPatch;
  /**
   * What the operator fine-tuned in Review › Adjust, kept APART from the
   * steps' own writes and REPLACED whole on every journal change.
   *
   * Two defects live in the seam this separation removes. Folded into
   * `designPatch` it could never be taken back out — `layoutPatch` merges and
   * never removes — so an Undo in the editor left its value behind, and the
   * next visit re-seeded the editor from a `designPatch` that already
   * contained the fine-tune, which made Undo a dead control. Kept here, an
   * Undo simply produces a smaller lift and the key falls back to the step's
   * value (or the base layout) on its own.
   *
   * LAYOUT ONLY. The panel's colour and face rows are folded into `colors` /
   * `fonts` by the host — the wizard's own homes for them, and what
   * `payload.ts` reads — so a later Colors step can never be outranked by a
   * stale brand key here.
   */
  adjustLift?: EditPatch;
  /** How many entries the ADJUST journal holds. A step card is not a journal
   *  entry, so `layoutPatch` does not touch it — ownership of a stepped field
   *  is tracked per path in `accepted`/`edited`, not by this count. */
  designSteps: number;
  cueShape?: BrandSetupCueShape;
  chips: StudyChip[];
  /** chip.field → still applied. A "Reset" flips it false and restores the
   *  default, so the row can say what happened instead of vanishing. */
  accepted: Record<string, boolean>;
  /**
   * chip.field → the user typed/picked their OWN value here.
   *
   * Kept apart from `accepted` because the two answer different questions.
   * `accepted[f] === false` means "not the study's value any more" — a hand
   * edit AND a Reset both do that, and it is what stops a re-read taking the
   * field back. `edited[f]` means "the value on screen is the user's", which
   * is the only thing that decides whether Reset still has work to do. Folding
   * them into one flag greyed the Reset button out the moment someone typed a
   * hex, leaving no way back to the default.
   */
  edited: Record<string, boolean>;
}

/**
 * Brand Setup's own defaults. The colour + tracking values reproduce the
 * founding channel EXACTLY (see `Onboard.tsx` DEFAULTS), so a user who clicks
 * straight through gets the same render the classic wizard would have made.
 * The language default is the ONE deliberate difference: the public product's
 * first user is not an Arabic channel.
 */
export const BRAND_SETUP_DEFAULTS = {
  /** The founding caption gold — the one colour that paints accents. */
  accent: "#FFBF17",
  /** The founding caption cream. */
  ink: "#F2EBD8",
  /** Post canvas + supporting text. No video render effect (spec §2.2). */
  paper: "#0E0F13",
  muted: "#9CA3AF",
  trackingPx: 0.5,
} as const;

/** How many links + files one read can take. The wizard enforces it, the
 *  Source screen prints it, and the "left out" sentence counts against it —
 *  one constant so those three can never disagree. */
export const MAX_SOURCES = 8;

export const BRAND_SETUP_LANGUAGE: LanguagePreset =
  LANGUAGE_PRESETS.find((p) => p.code === "en") ?? DEFAULT_LANGUAGE;

/**
 * The faces a LEFT-TO-RIGHT brand starts on.
 *
 * The founding trio (AlHurra / Lalezar / Faseh) covers Arabic — AlHurra has NO
 * Latin glyphs at all, and Faseh drops `é` / `à` / `ç`. A channel created with
 * blank roles falls back to that trio server-side, so a French creator's first
 * video shipped its captions in whatever generic face the render container had
 * installed. These two are library faces (`LIBRARY_FONT_FACES`), so they are
 * registered for the render and previewable over `/api/fonts/:family`.
 *
 * RTL keeps blank roles on purpose: that is the founding channel, byte-identical.
 */
export const LATIN_DEFAULT_FONTS = { display: "DM Serif Display", body: "Poppins" } as const;

/** The two faces a language starts on. RTL = "" (the founding trio). */
export function defaultFontsForLanguage(language: LanguagePreset): { display: string; body: string } {
  return language.direction === "rtl" ? { display: "", body: "" } : { ...LATIN_DEFAULT_FONTS };
}

/** The faces the RENDER falls back to when a role is left blank — the founding
 *  trio (`DEFAULT_FONTS` in `src/lib/channels.ts`). Display drives the punch
 *  line, Body drives the caption. Named, never called "the default face": a
 *  user has to be able to see which face they are about to ship. */
export const FOUNDING_FONT_NAMES = { display: "Lalezar", body: "AlHurra" } as const;

/** The family each role will REALLY render in, blank roles resolved. */
export function effectiveFontNames(state: BrandSetupState): { display: string; body: string } {
  const seed = defaultFontsForLanguage(state.language);
  return {
    display: state.fonts.display.trim() || seed.display || FOUNDING_FONT_NAMES.display,
    body: state.fonts.body.trim() || seed.body || FOUNDING_FONT_NAMES.body,
  };
}

export function initialBrandSetupState(): BrandSetupState {
  return {
    step: "welcome",
    returnTo: null,
    keptAll: false,
    mode: null,
    brandName: "",
    slug: "",
    slugTouched: false,
    language: BRAND_SETUP_LANGUAGE,
    sources: [],
    colors: {
      accent: BRAND_SETUP_DEFAULTS.accent,
      ink: BRAND_SETUP_DEFAULTS.ink,
      paper: BRAND_SETUP_DEFAULTS.paper,
      muted: BRAND_SETUP_DEFAULTS.muted,
    },
    colorPair: null,
    fonts: defaultFontsForLanguage(BRAND_SETUP_LANGUAGE),
    // The speaker name starts on the language's own Display seed, not on the
    // caption face (the 2026-09-15 ruling) — so the mirror starts OFF.
    displaySame: false,
    trackingPx: BRAND_SETUP_DEFAULTS.trackingPx,
    logoKind: null,
    designSteps: 0,
    chips: [],
    accepted: {},
    edited: {},
  };
}

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

export type BrandSetupAction =
  | { type: "patch"; patch: Partial<BrandSetupState> }
  | { type: "colors"; colors: Partial<BrandSetupState["colors"]> }
  | { type: "fonts"; fonts: Partial<BrandSetupState["fonts"]> }
  | { type: "tracking"; px: number }
  | { type: "language"; language: LanguagePreset }
  /** Go to a screen. `returnTo` is set by Review's "Change" and CLEARED by
   *  every other jump (omitting it means "clear"), so a stale return home can
   *  never survive a walk. */
  | { type: "step"; step: StepId; returnTo?: StepId | null }
  /** One geometry decision, merged into the design. `label` is for the
   *  CALLER'S logging only — it is not stored, and the reducer never reads it. */
  | { type: "layoutPatch"; patch: NonNullable<EditPatch["layout"]>; label?: string }
  /**
   * The Adjust journal, lifted back whole. REPLACES `adjustLift` (an Undo
   * hands back a smaller patch and the key must actually go), and carries the
   * journal's length so Review can say how many changes are on the video.
   */
  | { type: "adjustLift"; patch: EditPatch["layout"] | undefined; steps: number }
  | { type: "logo"; dataUrl: string; kind: "upload" | "wordmark"; wordmark?: BrandSetupState["wordmark"] }
  /** "Keep all and continue" on the reading screen — `false` when the operator
   *  goes Back and takes "Walk me through each" instead. */
  | { type: "keepAll"; value: boolean }
  /** "Use our defaults instead" — drop the studied design, keep the sources. */
  | { type: "dropStudyPatch" }
  | { type: "study"; record: BrandStudyRecord }
  | { type: "resetChip"; field: string };

/**
 * Which studied fields a hand edit takes OWNERSHIP of.
 *
 * `accepted[field] === false` means "this one is the user's now" — set by a
 * Reset, and equally by typing a different hex or picking a different face.
 * `applyStudy` reads it, so re-reading a brand can never quietly take back a
 * value the user changed in between.
 */
const CLAIMS: Record<string, readonly string[]> = {
  accent: ["brand.captionAccent", "speakerNameColor"],
  ink: ["brand.captionColor"],
  display: ["fontRoles.speakerName", "fontRoles.punch"],
  body: ["fontRoles.caption"],
  tracking: ["layout.caption.letterSpacing"],
  language: ["language"],
  brandName: ["brandName"],
};

/** Take ownership of chip fields BY NAME. The layout steps write dotted paths
 *  that are already chip fields (`layout.caption.fontSizePx`), so they claim
 *  themselves instead of going through the CLAIMS aliases. */
function claimFields(
  state: BrandSetupState,
  fields: readonly string[],
): Pick<BrandSetupState, "accepted" | "edited"> {
  const accepted = { ...state.accepted };
  const edited = { ...state.edited };
  for (const field of fields) {
    accepted[field] = false;
    edited[field] = true;
  }
  return { accepted, edited };
}

function claim(
  state: BrandSetupState,
  ...keys: string[]
): Pick<BrandSetupState, "accepted" | "edited"> {
  return claimFields(state, keys.flatMap((k) => CLAIMS[k] ?? []));
}

/**
 * The chip-field paths a layout patch writes — `layout.<section>.<key>`, or
 * `layout.<section>` for a section that IS the value (overlays).
 *
 * This is what turns a step click into ownership: the study's chips are named
 * with exactly these paths, so claiming them is what makes a re-read leave the
 * operator's size alone AND makes the chip row say "yours", not "applied".
 */
function layoutPatchFields(patch: NonNullable<EditPatch["layout"]>): string[] {
  const out: string[] = [];
  for (const [section, value] of Object.entries(patch)) {
    if (value !== null && typeof value === "object" && !Array.isArray(value))
      for (const key of Object.keys(value)) out.push(`layout.${section}.${key}`);
    else out.push(`layout.${section}`);
  }
  return out;
}

/** True when the user has made this studied field their own. */
function isOwned(state: BrandSetupState, ...fields: string[]): boolean {
  return fields.some((f) => state.accepted[f] === false);
}

/**
 * The same question for a layout PATH. `accepted === false` covers a Reset and
 * a claim; `edited === true` covers a value the user typed or a card they
 * clicked. Either one means "this number is the user's".
 */
function isOwnedPath(state: BrandSetupState, path: string): boolean {
  return state.accepted[path] === false || state.edited[path] === true;
}

/**
 * The part of a layout that belongs to the USER — the paths they own, and
 * nothing else.
 *
 * This is what a re-read merges back on top of the new study. Keeping the
 * WHOLE existing layout looked right and was not: "existing" includes the
 * PREVIOUS study's untouched numbers, so a second read of a different reel
 * kept the first reel's caption size while its own chip cheerfully read
 * "130 · applied". Only a path the operator actually made theirs survives a
 * re-read; every other path is the new study's to answer.
 */
function ownedLayoutLayer(
  state: BrandSetupState,
  layout: NonNullable<EditPatch["layout"]>,
): NonNullable<EditPatch["layout"]> {
  const out: Record<string, unknown> = {};
  for (const [section, value] of Object.entries(layout)) {
    if (value !== null && typeof value === "object" && !Array.isArray(value)) {
      const kept: Record<string, unknown> = {};
      for (const [key, inner] of Object.entries(value))
        if (isOwnedPath(state, `layout.${section}.${key}`)) kept[key] = inner;
      if (Object.keys(kept).length > 0) out[section] = kept;
    } else if (isOwnedPath(state, `layout.${section}`)) {
      out[section] = value;
    }
  }
  return out as NonNullable<EditPatch["layout"]>;
}

/**
 * Switch caption language, and RE-SEED any font role the user has not picked
 * for themselves. Script and face are one decision: moving an Arabic channel
 * to French has to move the faces too, or the captions render in a face with
 * no Latin glyphs. A role the user chose by hand is never touched.
 */
function applyLanguage(state: BrandSetupState, language: LanguagePreset): BrandSetupState {
  const seed = defaultFontsForLanguage(language);
  const wasSeed = defaultFontsForLanguage(state.language);
  const keepDisplay = isOwned(state, "fontRoles.speakerName", "fontRoles.punch") || state.fonts.display !== wasSeed.display;
  const keepBody = isOwned(state, "fontRoles.caption") || state.fonts.body !== wasSeed.body;
  return {
    ...state,
    language,
    fonts: {
      ...state.fonts,
      display: keepDisplay ? state.fonts.display : seed.display,
      body: keepBody ? state.fonts.body : seed.body,
    },
    ...claim(state, "language"),
  };
}

/**
 * One geometry decision folded into the design, SECTION BY SECTION.
 *
 * The steps write the same layout in three passes (size, then position, then
 * the speaker's corner), so a shallow `{...prev, ...patch}` would let the
 * position step wipe the size step's `fontSizePx`. `overlays` is the one key
 * that REPLACES rather than merges — the overlay list has replace semantics
 * everywhere else in the codebase (`applyEditState`, `buildLayoutPayload`),
 * and `[]` is a deliberate CLEAR, not an empty merge.
 *
 * Every object and array in the result is COPIED — the ones the patch brings
 * AND the ones only `prev` had — so neither a caller's memoized patch nor a
 * study record's own object can be aliased into state and mutated later.
 *
 * Also used the other way round — `applyStudy` merges the user's layout over
 * the studied one, so the same "sections merge, overlays replace" rule decides
 * a re-read.
 */
function mergeLayoutPatch(
  prev: EditPatch | undefined,
  patch: NonNullable<EditPatch["layout"]>,
): EditPatch {
  const merged: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(prev?.layout ?? {}))
    merged[key] = Array.isArray(value) ? [...value] : value !== null && typeof value === "object" ? { ...value } : value;
  for (const [key, value] of Object.entries(patch)) {
    const current = merged[key];
    const isSection = key !== "overlays" && value !== null && typeof value === "object" && !Array.isArray(value);
    if (!isSection) {
      merged[key] = Array.isArray(value) ? [...value] : value;
      continue;
    }
    const base = current !== null && typeof current === "object" && !Array.isArray(current) ? current : {};
    merged[key] = { ...(base as object), ...(value as object) };
  }
  return { ...prev, layout: merged as EditPatch["layout"] };
}

/**
 * `adjustLift` as a state slice — PRESENT or genuinely absent.
 *
 * `{ adjustLift: undefined }` is not the same as no key at all: the draft
 * serializer and every `"adjustLift" in state` check read the present-but-
 * undefined key as "there is a lift", which is how an emptied lift survived a
 * refresh as an object with nothing in it.
 */
function withLift<T extends BrandSetupState>(next: T, patch: EditPatch | undefined): T {
  if (patch && Object.keys(patch).length > 0) {
    next.adjustLift = patch;
    return next;
  }
  // REMOVE, never set to undefined: a spread of `{ adjustLift: undefined }`
  // leaves the previous lift standing, which is how an undone edit came back.
  delete next.adjustLift;
  return next;
}

/** The lift minus every path `patch` writes. Sections shrink, an emptied
 *  section goes, and an emptied lift comes back as `undefined`. */
export function dropLayoutKeys(
  lift: EditPatch | undefined,
  patch: NonNullable<EditPatch["layout"]>,
): EditPatch | undefined {
  const layout = lift?.layout;
  if (!layout) return lift;
  const out: Record<string, unknown> = {};
  for (const [section, value] of Object.entries(layout)) {
    const incoming = (patch as Record<string, unknown>)[section];
    if (incoming === undefined) {
      out[section] = value;
      continue;
    }
    // `overlays` (and anything else that is not a section of keys) is replaced
    // wholesale by the writer, so the lift has nothing left to say about it.
    if (value === null || typeof value !== "object" || Array.isArray(value)) continue;
    if (incoming === null || typeof incoming !== "object" || Array.isArray(incoming)) continue;
    const kept = Object.fromEntries(
      Object.entries(value as object).filter(([k]) => !(k in (incoming as object))),
    );
    if (Object.keys(kept).length > 0) out[section] = kept;
  }
  const rest = { ...lift };
  delete rest.layout;
  if (Object.keys(out).length === 0) {
    return Object.keys(rest).length > 0 ? (rest as EditPatch) : undefined;
  }
  return { ...rest, layout: out as EditPatch["layout"] };
}

/**
 * The design the preview and the payload actually render: the steps' own
 * geometry with the operator's fine-tuning laid over it.
 *
 * One function, so the phone, every step's "which card is lit", Review's
 * summary and `buildLayoutPayload` can never disagree about what the brand
 * currently looks like.
 */
export function effectiveDesign(state: BrandSetupState): EditPatch | undefined {
  const lift = state.adjustLift?.layout;
  if (!lift) return state.designPatch;
  const merged = mergeLayoutPatch(state.designPatch, lift);
  return Object.keys(merged).length > 0 ? merged : undefined;
}

/**
 * Hold the "Same as captions" promise. Applied after EVERY action so no path
 * can leave the two faces disagreeing — a language switch re-seeds Display,
 * and this puts the caption face back over it while the mirror is on.
 */
function mirrorDisplayFont(s: BrandSetupState): BrandSetupState {
  if (!s.displaySame || s.fonts.display === s.fonts.body) return s;
  return { ...s, fonts: { ...s.fonts, display: s.fonts.body } };
}

export function brandSetupReducer(state: BrandSetupState, action: BrandSetupAction): BrandSetupState {
  return mirrorDisplayFont(reduce(state, action));
}

function reduce(state: BrandSetupState, action: BrandSetupAction): BrandSetupState {
  switch (action.type) {
    case "patch":
      return {
        ...state,
        ...action.patch,
        ...(action.patch.brandName !== undefined ? claim(state, "brandName") : {}),
      };
    case "colors":
      return {
        ...state,
        colors: { ...state.colors, ...action.colors },
        ...claim(
          state,
          ...(action.colors.accent !== undefined ? ["accent"] : []),
          ...(action.colors.ink !== undefined ? ["ink"] : []),
        ),
      };
    case "fonts":
      return {
        ...state,
        fonts: { ...state.fonts, ...action.fonts },
        // Naming a face for the speaker IS the answer to "Same as captions?",
        // so the mirror stands down rather than overwriting the pick.
        ...(action.fonts.display !== undefined ? { displaySame: false } : {}),
        ...claim(
          state,
          ...(action.fonts.display !== undefined ? ["display"] : []),
          ...(action.fonts.body !== undefined ? ["body"] : []),
        ),
      };
    case "tracking":
      return { ...state, trackingPx: action.px, ...claim(state, "tracking") };
    case "language":
      return applyLanguage(state, action.language);
    case "step":
      return { ...state, step: action.step, returnTo: action.returnTo ?? null };
    case "layoutPatch":
      // A STEP answering a key the operator had fine-tuned is a later answer to
      // the same question, so the fine-tune stands down. Without this the lift
      // would outrank every subsequent step click for that key.
      return withLift({
        ...state,
        designPatch: mergeLayoutPatch(state.designPatch, action.patch),
        // A touched card is the user's. Claiming the exact chip paths it wrote
        // is what stops a re-read taking the size back (`applyStudy` merges
        // the studied layout UNDER this one) and what makes the chip row read
        // "yours" instead of "applied". `designSteps` is NOT bumped — that
        // number is the Adjust journal's length, not a count of step clicks.
        ...claimFields(state, layoutPatchFields(action.patch)),
      }, dropLayoutKeys(state.adjustLift, action.patch));
    case "adjustLift": {
      const patch = action.patch && Object.keys(action.patch).length > 0 ? { layout: action.patch } : undefined;
      return withLift({
        ...state,
        designSteps: action.steps,
        // The fine-tuned paths ARE the operator's, exactly like a step card's:
        // claiming them is what stops a re-read of the brand taking them back.
        ...claimFields(state, patch ? layoutPatchFields(action.patch!) : []),
      }, patch);
    }
    case "logo": {
      const next = { ...state, logoDataUrl: action.dataUrl, logoKind: action.kind };
      // The parameters belong to the wordmark. An uploaded logo takes its
      // place, so keeping them would redraw a logo nobody asked for on the
      // next restore.
      if (action.wordmark) next.wordmark = action.wordmark;
      else delete next.wordmark;
      return next;
    }
    case "keepAll":
      return { ...state, keptAll: action.value };
    case "dropStudyPatch":
      return dropStudyPatch(state);
    case "study":
      return applyStudy(state, action.record);
    case "resetChip":
      return resetChip(state, action.field);
  }
}

// ---------------------------------------------------------------------------
// Study → state
// ---------------------------------------------------------------------------

/** "#rrggbb" → "#RRGGBB", or null. */
function hex(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const t = v.trim().toUpperCase();
  return /^#[0-9A-F]{6}$/.test(t) ? t : null;
}

/** "-6px" → -6. Returns null for anything else (em, %, garbage). */
export function parsePx(v: unknown): number | null {
  if (typeof v !== "string") return null;
  const m = /^(-?\d+(?:\.\d+)?)px$/.exec(v.trim());
  return m ? Number(m[1]) : null;
}

/** A BCP-47-ish code → a preset, or a custom preset built from the code. */
export function languageForCode(code: string): LanguagePreset {
  const c = code.trim().toLowerCase();
  const preset = LANGUAGE_PRESETS.find((p) => p.code === c);
  if (preset) return preset;
  return makeCustomLanguage(c.toUpperCase(), c, directionForCode(c));
}

/**
 * Fold a finished study into the wizard state.
 *
 * Rules that are NOT negotiable:
 *   - the user's typed brand name always wins over the model's guess (they
 *     typed it one screen ago; a guess must never overwrite it);
 *   - a field the user has EDITED or RESET stays theirs. Brand Setup lets the
 *     brand be re-read at any point from screen 01, and a re-read that quietly
 *     reverted hand-picked colours would be the worst kind of bug: invisible
 *     until the render. Ownership is tracked in `accepted` (see CLAIMS);
 *   - the patch's `brand` colours become the Colors screen's values, and are
 *     DROPPED from `designPatch`, so Colors stays the single source of truth
 *     for colour (otherwise the layer below would silently re-apply them);
 *   - everything else in the patch rides on as `designPatch`, exactly like a
 *     Studio journal, so the geometry steps open on the brand's own numbers —
 *     merged UNDER whatever is already there, FIELD BY FIELD. The guard used
 *     to be all-or-nothing ("the canvas is the user's once they touch it"),
 *     which meant one touched card silently threw the whole studied layout
 *     away while its chips still read "applied". Now the user's keys win and
 *     the study's other keys still land — and a key the user set is claimed
 *     (`accepted false`), so the row says whose it is.
 */
export function applyStudy(state: BrandSetupState, record: BrandStudyRecord): BrandSetupState {
  const patch = record.patch ?? {};
  const extras = record.extras ?? {};
  const chips = record.chips ?? [];

  const accent = isOwned(state, "brand.captionAccent", "speakerNameColor")
    ? state.colors.accent
    : hex(patch.brand?.captionAccent) ?? hex(extras.speakerNameColor) ?? state.colors.accent;
  const ink = isOwned(state, "brand.captionColor")
    ? state.colors.ink
    : hex(patch.brand?.captionColor) ?? state.colors.ink;

  const body = isOwned(state, "fontRoles.caption")
    ? state.fonts.body
    : extras.fontRoles?.caption?.trim() || state.fonts.body;
  const display = isOwned(state, "fontRoles.speakerName", "fontRoles.punch")
    ? state.fonts.display
    : extras.fontRoles?.speakerName?.trim() || extras.fontRoles?.punch?.trim() || state.fonts.display;

  const studiedTracking =
    typeof extras.letterSpacingPx === "number" && Number.isFinite(extras.letterSpacingPx)
      ? extras.letterSpacingPx
      : parsePx(patch.layout?.caption?.letterSpacing);
  const tracking = isOwned(state, "layout.caption.letterSpacing")
    ? state.trackingPx
    : studiedTracking ?? state.trackingPx;

  const language = isOwned(state, "language")
    ? state.language
    : extras.language
      ? languageForCode(extras.language)
      : state.language;

  const guessedName = extras.brandName?.trim() ?? "";
  const brandName = state.brandName.trim() ? state.brandName : guessedName;

  // `brand` is consumed by the Colors screen; keeping it in designPatch too
  // would apply the same colour twice (and survive a Reset). The studied
  // layout goes UNDER the paths the user OWNS — see the note above, and
  // `ownedLayoutLayer` for why it is ownership and not mere presence.
  const designPatch: EditPatch | undefined = patch.layout
    ? mergeLayoutPatch(
        { ...state.designPatch, layout: patch.layout },
        ownedLayoutLayer(state, state.designPatch?.layout ?? {}),
      )
    : state.designPatch;

  // Fresh chips start applied, but every field the user already claimed stays
  // claimed — otherwise a re-read would silently re-accept it.
  const owned = Object.entries(state.accepted).filter(([, v]) => v === false);
  const accepted: BrandSetupState["accepted"] = {
    ...Object.fromEntries(chips.map((c) => [c.field, true])),
    ...Object.fromEntries(owned),
  };

  return {
    ...state,
    study: record,
    studyId: record.id,
    brandName,
    language,
    colors: { ...state.colors, accent, ink },
    fonts: { ...state.fonts, display, body },
    trackingPx: tracking,
    ...(extras.cueShape && !isOwned(state, "cueShape.maxDurSec", "cueShape.accentWords")
      ? { cueShape: extras.cueShape }
      : {}),
    ...(designPatch ? { designPatch } : {}),
    chips,
    accepted,
  };
}

// ---------------------------------------------------------------------------
// Reset one proposal
// ---------------------------------------------------------------------------

/** Drop `layout.a.b` (or `layout.a`) from a patch; returns a copy. */
function dropLayoutPath(patch: EditPatch | undefined, field: string): EditPatch | undefined {
  if (!patch?.layout) return patch;
  const seg = field.split(".");
  if (seg[0] !== "layout" || seg.length < 2) return patch;
  const layout = { ...patch.layout } as Record<string, unknown>;
  const section = seg[1]!;
  if (seg.length === 2) {
    delete layout[section];
  } else {
    const inner = layout[section];
    if (inner && typeof inner === "object" && !Array.isArray(inner)) {
      const copy = { ...(inner as Record<string, unknown>) };
      delete copy[seg[2]!];
      if (Object.keys(copy).length === 0) delete layout[section];
      else layout[section] = copy;
    }
  }
  const next: EditPatch = { ...patch, layout: layout as EditPatch["layout"] };
  if (Object.keys(layout).length === 0) delete next.layout;
  return Object.keys(next).length === 0 ? undefined : next;
}

/**
 * Put ONE studied field back to the Brand Setup default. Reset never deletes
 * the chip — it flips it to "not applied", so the row keeps saying what we
 * read and what we did with it.
 */
export function resetChip(state: BrandSetupState, field: string): BrandSetupState {
  const accepted = { ...state.accepted, [field]: false };
  // Reset hands the field back to the default, so it is no longer the user's
  // OWN value — that is exactly what re-arms the button for the next read.
  const edited = { ...state.edited, [field]: false };
  const seed = defaultFontsForLanguage(state.language);
  switch (field) {
    case "brand.captionAccent":
    case "speakerNameColor":
      return { ...state, accepted, edited, colors: { ...state.colors, accent: BRAND_SETUP_DEFAULTS.accent } };
    case "brand.captionColor":
      return { ...state, accepted, edited, colors: { ...state.colors, ink: BRAND_SETUP_DEFAULTS.ink } };
    case "fontRoles.caption":
      return { ...state, accepted, edited, fonts: { ...state.fonts, body: seed.body } };
    case "fontRoles.speakerName":
    case "fontRoles.punch":
      return { ...state, accepted, edited, fonts: { ...state.fonts, display: seed.display } };
    case "language": {
      const next = applyLanguage(state, BRAND_SETUP_LANGUAGE);
      return { ...next, accepted, edited };
    }
    case "brandName":
      return { ...state, accepted, edited, brandName: "" };
    default:
      break;
  }
  if (field.startsWith("cueShape.")) {
    const next = { ...state, accepted, edited };
    delete next.cueShape;
    return next;
  }
  if (field === "layout.caption.letterSpacing") {
    // Same REMOVE-don't-undefine rule as the generic layout branch below: a
    // present-but-undefined `designPatch` key reads as "there is a patch" to
    // anything doing a key check on the way to the draft.
    const designPatch = dropLayoutPath(state.designPatch, field);
    const next = { ...state, accepted, edited, trackingPx: BRAND_SETUP_DEFAULTS.trackingPx };
    if (designPatch) next.designPatch = designPatch;
    else delete next.designPatch;
    return next;
  }
  if (field.startsWith("layout.")) {
    const designPatch = dropLayoutPath(state.designPatch, field);
    // The fine-tuned copy goes with it: a row that says "back to the default"
    // must not leave the editor's value painting over that default.
    const adjustLift = dropLayoutPath(state.adjustLift, field);
    const next = { ...state, accepted, edited };
    if (designPatch) next.designPatch = designPatch;
    else delete next.designPatch;
    if (adjustLift) next.adjustLift = adjustLift;
    else delete next.adjustLift;
    return next;
  }
  return { ...state, accepted, edited };
}

/**
 * "Use our defaults instead" — the link on the reading screen (§2.2).
 *
 * It means ALL of it, not just the geometry: every studied colour, face,
 * tracking value, pacing shape and layout path goes back to the wizard's own
 * default. `resetChip` already knows every one of those defaults and already
 * flips `accepted`/`edited`, so this is that walk over the chip list — one
 * implementation, no second opinion about what "default" means.
 *
 * Two exclusions, both about not throwing away an answer:
 *   - `brandName` and `language` were ANSWERED by the user on `basics`, one
 *     screen before any of this. Reverting them would blank a typed name and
 *     move the caption language the operator chose.
 *   - a chip the user has since EDITED by hand is not the study's to revert
 *     (that is what `edited` means — see the note on the field).
 * Both are still marked not-applied, so a later re-read can't put the study's
 * version back over them either.
 *
 * `keptAll` and `colorPair` are cleared with the values: "keep all" is no
 * longer true, and the Colors step's lit-up pair was the study's.
 * The sources and the study record STAY — the operator can change their mind
 * without pasting the reel again.
 */
export function dropStudyPatch(state: BrandSetupState): BrandSetupState {
  const reverted = state.chips.reduce((acc, chip) => {
    const field = chip.field;
    const answered = field === "brandName" || field === "language";
    if (answered || state.edited[field] === true)
      return { ...acc, accepted: { ...acc.accepted, [field]: false } };
    return resetChip(acc, field);
  }, state);
  return { ...reverted, colorPair: null, keptAll: false };
}

/** The chip-field prefix every logo geometry chip shares. */
export const LOGO_CHIP_PREFIX = "layout.logo.";

/**
 * Did the study see a logo on the reel, and is that reading still applied?
 *
 * True while an accepted chip names a logo geometry path — the mapper writes
 * those only when the pixels measured a logo or the model saw one. It is what
 * keeps the "YOUR LOGO" placeholder in the reel's spot through the logo step
 * instead of drawing the wordmark on arrival (spec round-2 §2.3); a Reset on
 * that step flips every logo chip and the wordmark takes over as before.
 */
export function studyFoundLogo(state: Pick<BrandSetupState, "chips" | "accepted">): boolean {
  return state.chips.some((c) => c.field.startsWith(LOGO_CHIP_PREFIX) && state.accepted[c.field] !== false);
}

/** Which screen a chip belongs on, so each step shows only its own rows. */
export function chipScope(field: string): "colors" | "type" | "layout" {
  if (field === "brand.captionColor" || field === "brand.captionAccent" || field === "speakerNameColor") return "colors";
  if (field.startsWith("fontRoles.") || field === "layout.caption.textTransform" || field === "layout.caption.letterSpacing" || field === "language") return "type";
  return "layout";
}
